import React, { FC, useEffect } from "react";
import { useQuery, useReactiveVar } from "@apollo/client";
import { Navigate } from "react-router-dom";
import { currentBranchIdVar, currentUserEmailVar } from "@me-team/host/main/globalVar/state";
import { MeUserDocument } from "@me-team/host/main/globalVar/graphql/globalVar.hooks";
import {
    useGetBranchesIsDeletedQuery,
    useGetLoginCompaniesQuery,
} from "@me-team/host/main/company/graphql/company.hooks";
import { Spinner } from "@services/ui-components";
import { LoginCompany } from "@me-team/host/main/graphql/types";
import { useGetCurrentBranchIdQuery } from "@me-team/host/main/branches/graphql/branches.hooks";

interface ProtectedRegistrationRouteProps {
    element: React.ComponentType;
}

const ProtectedRegistrationRoute: FC<ProtectedRegistrationRouteProps> = ({ element: Component }) => {
    const { loading, error } = useQuery(MeUserDocument);
    const currentUserEmail = useReactiveVar(currentUserEmailVar);

    const { data: companiesData, loading: loadingCompaniesData } = useGetLoginCompaniesQuery({
        skip: !currentUserEmail,
        variables: { email: currentUserEmail },
        context: {
            errorType: "global",
        },
    });

    const companyWithoutOwner: LoginCompany = companiesData?.loginCompanies?.find((c) => !c.employee);
    const companyWithOwner: LoginCompany = companiesData?.loginCompanies?.find((c) => c.employee);
    const companyId = companyWithoutOwner?.id || companyWithOwner?.id;

    const { data: currentBranchData, loading: currentBranchLoading } = useGetCurrentBranchIdQuery({
        skip: !companyId,
    });
    const currentBranchId = currentBranchData?.user?.currentBranch?.id;

    const { data: branchData, loading: branchesLoading } = useGetBranchesIsDeletedQuery({
        skip: !companyId || !!currentBranchId,
        variables: {
            isDeleted: false,
            companyId: Number(companyId),
        },
        context: {
            errorType: "local",
        },
    });

    const branchId = currentBranchId || branchData?.user?.company?.branches?.[0]?.id;

    useEffect(() => {
        if (companyWithOwner && branchId) {
            currentBranchIdVar(branchId);
        }
    }, [companyWithOwner, branchId]);

    if (error) {
        console.error("Error fetching user data:", error);
        return <div>Error loading data</div>;
    }

    if (loading || loadingCompaniesData || branchesLoading || currentBranchLoading) return <Spinner />;

    if (companyWithoutOwner) {
        return <Navigate to={`/create-owner/${branchId}`} replace />;
    }

    if (companyWithOwner) {
        return <Navigate to={`/branches/main/branch/${branchId}`} replace />;
    }

    return <Component />;
};

export default ProtectedRegistrationRoute;
